// ============================================================
// TextOverlayTool.jsx — timed text overlays (v1)
// ------------------------------------------------------------
// Each overlay has text, an in/out timestamp (seconds), a frame position
// (x/y percent), and basic styling. The active overlay can be dragged on the
// canvas; here we edit its text, timing, and style.
// ============================================================

import React from 'react';
import { fmtTime } from '../../utils/mockVideoAssets';
import { VIDEO_FONTS, DEFAULT_FONT_ID, fontFamilyFor } from '../../utils/videoFonts';

const ALIGNS = [
  { id: 'left', label: '⬅' },
  { id: 'center', label: '⬌' },
  { id: 'right', label: '➡' },
];

// Text entrance/exit animations — wired to FFmpeg drawtext alpha/y expressions
// in the backend (buildRendiFfmpeg) and shown here as a simple selector.
const ANIMATIONS = [
  { id: 'none',     label: 'None' },
  { id: 'fadeIn',   label: 'Fade in' },
  { id: 'fadeOut',  label: 'Fade out' },
  { id: 'fadeBoth', label: 'Fade in + out' },
  { id: 'slideUp',  label: 'Slide up' },
  { id: 'slideDown', label: 'Slide down' },
];

export default function TextOverlayTool({
  overlays = [],
  activeId,
  duration = 0,
  onAdd,
  onSelect,
  onUpdate,
  onRemove,
  onAddSubtitles,
  subtitlesLoading = false,
}) {
  const active = overlays.find((o) => o.id === activeId) || null;
  const max = duration || (active ? active.outTime : 0) || 0;
  const hasSubtitles = overlays.some((o) => o.source === 'subtitle');

  return (
    <div className="ve-text">
      <button className="ve-btn ve-btn-accent ve-btn-block" onClick={() => onAdd?.()}>
        + Add text overlay
      </button>

      {/* Subtitles sit with the other overlay controls because that is exactly
          what they become — the inserted segments appear in the layer list
          below and are edited like any other overlay. Deliberately worded
          "Subtitles", never "Caption": "✦ Generate Caption" is a different
          feature in the Post Generator that writes the post copy. */}
      <button
        className="ve-btn ve-btn-accent ve-btn-block"
        onClick={() => onAddSubtitles?.()}
        disabled={subtitlesLoading}
        title="Transcribe the spoken audio into timed text overlays"
      >
        {subtitlesLoading ? 'Transcribing…' : hasSubtitles ? '↻ Redo subtitles' : '+ Add subtitles'}
      </button>
      <p className="ve-hint" style={{ margin: '6px 0 0' }}>
        {subtitlesLoading
          ? 'Listening to the audio — this can take a moment on a long clip.'
          : hasSubtitles
            ? 'Re-running replaces the current subtitles. Your own text overlays are kept.'
            : 'Transcribes the audio and adds one timed overlay per line. Edit or reposition them like any other text.'}
      </p>

      {overlays.length === 0 && (
        <p className="ve-hint">No overlays yet. Add one, then drag it on the preview to position it.</p>
      )}

      {overlays.length > 0 && (
        <p className="ve-hint" style={{ margin: '10px 0 0' }}>
          Selected text stays on the preview so you can position it — click the video
          background or press Esc to deselect.
        </p>
      )}

      <div className="ve-layer-list">
        {overlays.map((o) => (
          <div
            key={o.id}
            className={`ve-layer-row${o.id === activeId ? ' active' : ''}`}
            onClick={() => onSelect?.(o.id)}
            title={o.id === activeId ? 'Click again to deselect' : 'Select to edit and position'}
          >
            <span className="ve-layer-text">{o.text || '(empty)'}</span>
            <span className="ve-layer-time">{fmtTime(o.inTime)}–{fmtTime(o.outTime)}</span>
            <button
              className="ve-layer-del"
              onClick={(e) => { e.stopPropagation(); onRemove?.(o.id); }}
              title="Delete overlay"
            >×</button>
          </div>
        ))}
      </div>

      {active && (
        <div className="ve-text-editor">
          <div className="ve-field-label">Text</div>
          <textarea
            className="ve-text-input"
            rows={2}
            value={active.text}
            onChange={(e) => onUpdate?.(active.id, { text: e.target.value })}
          />

          <div className="ve-field-label">Font</div>
          <select
            className="ve-select"
            value={active.font || DEFAULT_FONT_ID}
            onChange={(e) => onUpdate?.(active.id, { font: e.target.value })}
            style={{ fontFamily: fontFamilyFor(active.font || DEFAULT_FONT_ID) }}
          >
            {VIDEO_FONTS.map((f) => (
              <option key={f.id} value={f.id} style={{ fontFamily: f.family }}>
                {f.label}
              </option>
            ))}
          </select>

          <div className="ve-field-label">Timing (seconds)</div>
          <div className="ve-time-row">
            <label className="ve-time-field">
              <span>In</span>
              <input
                type="number" min={0} max={max} step={0.1}
                value={Number(active.inTime.toFixed(1))}
                onChange={(e) => {
                  const v = Math.max(0, Math.min(active.outTime - 0.1, Number(e.target.value)));
                  onUpdate?.(active.id, { inTime: v });
                }}
              />
            </label>
            <label className="ve-time-field">
              <span>Out</span>
              <input
                type="number" min={0} max={max} step={0.1}
                value={Number(active.outTime.toFixed(1))}
                onChange={(e) => {
                  const cap = max || Number(e.target.value);
                  const v = Math.min(cap, Math.max(active.inTime + 0.1, Number(e.target.value)));
                  onUpdate?.(active.id, { outTime: v });
                }}
              />
            </label>
          </div>

          <div className="ve-field-label">Size</div>
          <div className="ve-slider-row">
            <input
              className="ve-range" type="range" min={16} max={96} step={1}
              value={active.size}
              onChange={(e) => onUpdate?.(active.id, { size: Number(e.target.value) })}
            />
            <span className="ve-slider-val">{active.size}px</span>
          </div>

          {/* Text color — full color picker, matching the carousel builder */}
          <div className="ve-field-label">Colour</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4 }}>
            <input
              type="color"
              value={active.color || '#ffffff'}
              onChange={(e) => onUpdate?.(active.id, { color: e.target.value })}
              style={{
                width: 28, height: 24, padding: 0, border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 4, background: 'transparent', cursor: 'pointer',
              }}
            />
            <span style={{ fontSize: 10, color: 'var(--text-muted)', fontWeight: 600 }}>
              Color · {(active.color || '#ffffff').toUpperCase()}
            </span>
          </div>

          <div className="ve-field-label">Align</div>
          <div className="ve-align-row">
            {ALIGNS.map((a) => (
              <button
                key={a.id}
                className={`ve-align-btn${active.align === a.id ? ' active' : ''}`}
                onClick={() => onUpdate?.(active.id, { align: a.id })}
              >{a.label}</button>
            ))}
          </div>

          <div className="ve-field-label">Animation</div>
          <select
            className="ve-select"
            value={active.animationType || 'none'}
            onChange={(e) => onUpdate?.(active.id, { animationType: e.target.value })}
          >
            {ANIMATIONS.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}
